import {AxiosError} from 'axios'
import {useQuery, UseQueryResult} from 'react-query'

import {OrderIntakeOptionsDto} from '@hconnect/tuc-processes/orderIntake'

import {useFeaturesState} from '../../../../Organisms/Features'
import {
  ORDER_CREATE_API_PATH,
  V2_ORDER_REQUESTS_API_PATH
} from '../../../../Organisms/OrderIntake/OrderIntake.action'
import {fetchRmcOrderIntakeOptionsV2} from '../services/rmcOrderIntakeOptionsService'

/**
 * Hook to fetch RMC order intake options from the v2/OrderRequests API
 * (or order-create API when EnableOrderCreateApi toggle is on)
 * Uses React Query for data fetching, caching, and error handling.
 *
 * @param customerId The customer ID from the selected job site
 * @param siteId The site ID from the selected job site
 * @param salesOffice The sales office from the selected job site
 * @param marketArea The market area from the selected job site
 * @param enabled Whether the query should run (default: true if both IDs are provided)
 * @returns UseQueryResult with RMC order intake options
 */
export const useRmcOrderIntakeOptionsV2 = (
  customerId: string | undefined,
  siteId: string | undefined,
  salesOffice?: string,
  marketArea?: string,
  enabled: boolean = true
): UseQueryResult<OrderIntakeOptionsDto, AxiosError> => {
  const {getFeature} = useFeaturesState()
  const enableOrderCreateApi = !!getFeature('EnableOrderCreateApi')
  const apiPath = enableOrderCreateApi ? ORDER_CREATE_API_PATH : V2_ORDER_REQUESTS_API_PATH
  const shouldFetch = enabled && !!customerId && !!siteId

  return useQuery<OrderIntakeOptionsDto, AxiosError>(
    [
      'rmcOrderIntakeOptionsV2',
      customerId,
      siteId,
      'RMC',
      salesOffice,
      marketArea,
      enableOrderCreateApi
    ],
    () =>
      fetchRmcOrderIntakeOptionsV2(customerId!, siteId!, 'RMC', salesOffice, marketArea, apiPath),
    {
      enabled: shouldFetch,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: 2,
      retryDelay: (attempt) => Math.min(attempt > 1 ? 2000 : 1000, 30000),
      onError: (error) => {
        console.error('Error fetching RMC order intake options from v2 API:', error)
      }
    }
  )
}
